import * as React from 'react';
import Tag from 'antd/lib/tag';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import Tooltip from 'antd/lib/tooltip';
import Popover from 'antd/lib/popover';
import Button from 'antd/lib/button';
import { useSelector } from 'react-redux';
import { BranchesOutlined, FileTextOutlined, InfoCircleOutlined } from '@ant-design/icons';
import commitIcon from '../../../../assets/commit-icon.svg';
import {
    ArchetypeSpecificStepRunSummaryPanelProps,
    StepRunSummaryContainer,
    StepRunSummaryDetailItem,
    StepRunSummaryDetailSeparator,
} from '.';
import { BuildArtifact, ReleaseStepReleaseCandidateData } from '@/types';
import {
    buildStatusStringForBuild,
    statusColorForBuildStatus,
    shortCommitHashFromHash,
} from '@/helpers/displayHelpers';
import { bytesToMB } from '@/helpers';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import { dayjs } from '@/extendedDayjs';
import { getLocaleDateTimeFormat } from '@/timeHelpers';
import { BinaryBuildDetailsView } from '@/components/workflowModule/BinaryBuildDetailsView';
import { ArtifactInstallMenu } from '@/components/workflowModule/ArtifactsModule';
import {
    selectOrg,
    selectAppDistributionServices,
    selectAppIdentifiers,
    selectStabilityMonitoringIntegration,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { StabilityMonitoringService } from '@/types';

export const ReleaseCandidateStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({
    stepRun,
    appId,
    shouldDisplayCompactVersion,
}) => {
    const org = useSelector((state: RootState) => selectOrg(state));
    const orgId = org?.orgId;
    const distributionServices = useSelector((state: RootState) => selectAppDistributionServices(state, appId));
    const appIdentifiers = useSelector((state: RootState) => selectAppIdentifiers(state, appId));
    const stabilityMonitoring = useSelector((state: RootState) =>
        selectStabilityMonitoringIntegration(state, appId),
    ) as StabilityMonitoringService;

    const stepData: ReleaseStepReleaseCandidateData = {
        ...stepRun.data.summaryData,
        ...stepRun.data.complementaryData,
    };

    const builds = stepData.builds ?? [];
    const selectedBuild = builds.find((build) => build.isSelectedBuild);
    const ciBuild = selectedBuild?.ciBuild;
    const workflowData = stepData.workflowData;

    const [icon, alt] = iconAndAltForIntegrationType(workflowData?.integrationType);
    const buildStatusText = buildStatusStringForBuild(ciBuild);
    const tagColor = statusColorForBuildStatus(ciBuild?.buildStatus);

    const commit = ciBuild?.commit ?? workflowData?.headCommit ?? '--';
    const commitMessage = ciBuild?.commitMessage ?? workflowData?.headCommitMessage ?? '--';
    const branch = ciBuild?.branchName ?? workflowData?.branch ?? '--';

    const startedAtMoment = ciBuild?.buildStartedAt ? dayjs(ciBuild.buildStartedAt) : null;

    const formatStartedTime = () => {
        if (startedAtMoment) {
            return `started ${startedAtMoment.format(getLocaleDateTimeFormat('short'))}`;
        }
        return null;
    };

    const topBar = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Space align="center" size={12}>
                {icon && <img src={icon} alt={alt} style={{ height: 20 }} />}
                <Tag
                    style={{
                        ...titleStyle,
                        fontSize: 10,
                        lineHeight: '22px',
                        margin: 0,
                        color: tagColor,
                        background: 'white',
                        border: `1px solid ${tagColor}`,
                    }}
                >
                    {buildStatusText}
                </Tag>
                <Space split={<Divider type="vertical" style={{ backgroundColor: antGrey.grey5 }} />} align="center">
                    {ciBuild?.buildIdentifier &&
                        (ciBuild.buildUrl ?
                            <Link href={ciBuild.buildUrl} target="_blank" strong>
                                Build #{ciBuild.buildIdentifier}
                            </Link>
                        :   <Text style={{ color: antGrey.grey8 }} strong>
                                Build #{ciBuild.buildIdentifier}
                            </Text>)}
                    {formatStartedTime() && <Text style={{ color: antGrey.grey7 }}>{formatStartedTime()}</Text>}
                </Space>
            </Space>
            <Space align="center" size={8}>
                {workflowData?.workflowUrl ?
                    <Link href={workflowData.workflowUrl} target="_blank" style={{ color: runwayColors.info }}>
                        {workflowData.workflowName ?? '--'}
                    </Link>
                :   <Text style={{ color: runwayColors.info }}>{workflowData?.workflowName ?? '--'}</Text>}
                <Tooltip title="The workflow that generates your RC build.">
                    <InfoCircleOutlined style={{ color: antGrey.grey6, cursor: 'pointer' }} />
                </Tooltip>
            </Space>
        </div>
    );

    const formatCommitBody = () => {
        const shortHash = shortCommitHashFromHash(commit);
        return (
            <Space align="center" size={8}>
                <img src={commitIcon} alt="commit" style={{ width: 14, height: 14 }} />
                <Text style={{ color: antGrey.grey8, fontFamily: 'monospace' }}>{shortHash}</Text>
            </Space>
        );
    };

    const formatMessageBody = () => {
        return (
            <Text style={{ color: antGrey.grey8 }} ellipsis={{ tooltip: commitMessage }}>
                {commitMessage}
            </Text>
        );
    };

    const formatBranchBody = () => {
        return (
            <Space align="center" size={8}>
                <BranchesOutlined style={{ color: antGrey.grey7 }} />
                <Text style={{ color: antGrey.grey8, fontFamily: 'monospace' }}>{branch}</Text>
            </Space>
        );
    };

    const artifacts = ciBuild?.artifacts ?? [];

    const formatArtifactsBody = () => {
        if (artifacts.length === 0) {
            return <Text style={{ color: antGrey.grey7, fontStyle: 'italic' }}>No artifacts</Text>;
        }

        return (
            <Space
                direction="vertical"
                size={0}
                split={<Divider style={{ margin: '8px 0', backgroundColor: antGrey.grey4 }} />}
                style={{ width: '100%' }}
            >
                {artifacts.map((artifact: BuildArtifact, index: number) => {
                    const hasDetails =
                        artifact.binaryBuildDetails ||
                        (artifact.appStoreInfos && Object.keys(artifact.appStoreInfos).length > 0) ||
                        artifact.dsymUploadInfo;

                    const sizeInMB = bytesToMB(artifact.size);

                    return (
                        <div
                            key={index}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Space align="center" size={4}>
                                <FileTextOutlined style={{ color: antGrey.grey7 }} />
                                <Button
                                    type="link"
                                    style={{ padding: 0, fontWeight: 600 }}
                                    onClick={() => {
                                        if (artifact.downloadUrl) {
                                            window.open(artifact.downloadUrl, '_self');
                                        }
                                    }}
                                >
                                    {artifact.fileName}
                                </Button>
                                {sizeInMB > 0 && <Text type="secondary">({sizeInMB} MB)</Text>}
                                {hasDetails && (
                                    <Popover
                                        content={
                                            <BinaryBuildDetailsView
                                                artifact={artifact}
                                                showUploadStatus
                                                distributionServices={distributionServices}
                                                appIdentifiers={appIdentifiers}
                                                stabilityMonitoring={stabilityMonitoring}
                                            />
                                        }
                                        trigger="hover"
                                        placement="bottom"
                                    >
                                        <InfoCircleOutlined
                                            style={{ color: runwayColors.info, cursor: 'pointer', marginLeft: 4 }}
                                        />
                                    </Popover>
                                )}
                            </Space>
                            {artifact.binaryBuildDetails && orgId && (
                                <ArtifactInstallMenu orgId={orgId} appId={appId} artifact={artifact} />
                            )}
                        </div>
                    );
                })}
            </Space>
        );
    };

    return (
        <StepRunSummaryContainer topBar={topBar}>
            {!shouldDisplayCompactVersion && (
                <>
                    <StepRunSummaryDetailItem header="BRANCH" body={formatBranchBody()} />
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem header="COMMIT" body={formatCommitBody()} />
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem header="MESSAGE" body={formatMessageBody()} />
                    {artifacts.length > 0 && (
                        <>
                            <div style={{ flexBasis: '100%', height: 0 }} />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    padding: '12px 20px',
                                    width: '100%',
                                    flex: 1,
                                    borderTop: `1px solid ${antGrey.grey4}`,
                                }}
                            >
                                <Text
                                    style={{
                                        ...titleStyle,
                                        color: antGrey.grey7,
                                        fontSize: 10,
                                    }}
                                >
                                    ARTIFACTS
                                </Text>
                                {formatArtifactsBody()}
                            </div>
                        </>
                    )}
                </>
            )}
        </StepRunSummaryContainer>
    );
};
